import { AxiosError } from 'axios';
import { apiClient } from './config';
import { apiVariables } from './apiVariables';
import { getSensitiveData } from '../../utils/secureStorage';

export enum FeedbackType {
  Bug = 1,
  Suggestion = 2,
  General = 3,
  Rating = 4,
}

export interface FeedbackRequest {
  userId: string;
  feedbackType: FeedbackType;
  subject?: string;
  message: string;
  rating?: number;
  appVersion?: string;
  deviceInfo?: string;
}

export interface FeedbackResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface FeedbackItem {
  feedbackId: number;
  userId: string;
  userName?: string;
  feedbackTypeName?: string;
  subject?: string;
  message: string;
  rating?: number;
  appVersion?: string;
  deviceInfo?: string;
  statusName?: string;
  createdAt: string;
  adminNotes?: string;
}

export interface MyFeedbackResponse {
  success: boolean;
  message?: string;
  data?: FeedbackItem[];
}

/**
 * Submit end-user feedback
 * @param feedback - Feedback data to submit (userId is populated automatically if omitted)
 * @returns Promise with feedback response
 */
export const submitFeedback = async (
  feedback: Omit<FeedbackRequest, 'userId'> & { userId?: string }
): Promise<FeedbackResponse> => {
  try {
    const userId = feedback.userId || (await getSensitiveData('userId')) || '';

    const response = await apiClient.post(apiVariables.FEEDBACK_SUBMIT, {
      ...feedback,
      userId,
    });

    const data = response.data;
    return {
      success: true,
      message: data?.message || 'Feedback submitted successfully',
      data,
    };
  } catch (error) {
    console.error('Error submitting feedback:', error);
    return {
      success: false,
      message: extractErrorMessage(error, 'Failed to submit feedback'),
    };
  }
};

/**
 * Get feedback submitted by the current user
 * @returns Promise with the user's feedback items
 */
export const getMyFeedback = async (): Promise<MyFeedbackResponse> => {
  try {
    const response = await apiClient.get(`${apiVariables.FEEDBACK_SUBMIT}/mine`);
    const data = response.data;
    return { success: true, message: data?.message, data: data?.data || [] };
  } catch (error) {
    console.error('Error fetching feedback:', error);
    return { success: false, message: extractErrorMessage(error, 'Failed to fetch feedback') };
  }
};

/**
 * Edit feedback previously submitted by the current user (only while not yet reviewed)
 * @param feedbackId - Id of the feedback to update
 * @param feedback - Updated feedback fields
 * @returns Promise with feedback response
 */
export const updateFeedback = async (
  feedbackId: number,
  feedback: Omit<FeedbackRequest, 'userId' | 'appVersion' | 'deviceInfo'> & { userId?: string }
): Promise<FeedbackResponse> => {
  try {
    const userId = feedback.userId || (await getSensitiveData('userId')) || '';

    const response = await apiClient.put(`${apiVariables.FEEDBACK_SUBMIT}/${feedbackId}`, {
      ...feedback,
      userId,
    });

    const data = response.data;
    return { success: true, message: data?.message || 'Feedback updated successfully', data };
  } catch (error) {
    console.error('Error updating feedback:', error);
    return { success: false, message: extractErrorMessage(error, 'Failed to update feedback') };
  }
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.message || error.response?.data?.error || fallback;
  }
  return fallback;
};
